import React, { useState, useContext, useEffect } from "react";
import { GraphContext } from "../../../../../../../contexts/GraphContext";
import TagEditor from "../../../../../SavedTags/components/TagEditor/TagEditor";
import CompactTagsTable from "../../../../../SavedTags/components/TagTables/CompactTagsTable";
import { AutoSuggestedMetadata } from "../../AutoSuggest/AutoSuggestedMetadata";
import {
  FiX,
  FiArrowLeft,
  FiEdit3,
  FiBookOpen,
  FiZap,
  FiPlus,
} from "react-icons/fi";

export const AddNodeSidePanel = ({
  handleAddNodeApplied,
  generatingSchema,
  setGeneratingSchema,
}) => {
  const {
    setSidePanelOpen,
    selectedNodeData,
    setSelectedNodeData,
    selectedMode,
    setSelectedMode,
    GRAPH_PANEL_MODES,
  } = useContext(GraphContext);

  const [tagInEditor, setTagInEditor] = useState({});
  const [isTagEditorCollapsed, setIsTagEditorCollapsed] = useState(false);
  const [activeTagEditorSection, setActiveTagEditorSection] =
    useState("general");

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        handleClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClose = () => {
    setSelectedNodeData({});
    setSidePanelOpen(false);
    setSelectedMode(null);
  };
  const handleApply = async () => {
    if (!tagInEditor || !selectedNodeData) return;

    try {
      await handleAddNodeApplied(selectedNodeData, tagInEditor);
    } catch (error) {
      console.error("Error applying tag:", error);
    }
  };

  const ModeSelector = () => (
    <div className="flex flex-col">
      {/* Header */}
      <div className="px-6 py-5 border-b border-gray-100">
        <h2
          className="text-xl font-semibold text-gray-900 mb-2 pr-8"
          id="mode-selector-title"
        >
          Add Metadata Tag
        </h2>
        <p className="text-sm text-gray-600">
          Choose how you'd like to add a new metadata tag to your schema
        </p>
      </div>

      {/* Options */}
      <div
        className="p-6 space-y-4"
        role="radiogroup"
        aria-labelledby="mode-selector-title"
      >
        <button
          onClick={() => setSelectedMode(GRAPH_PANEL_MODES.MANUAL)}
          className="w-full group text-left px-5 py-4 hover:bg-blue-50 hover:border-blue-200 rounded-xl border-2 border-gray-200 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-300"
          role="radio"
          aria-checked="false"
          aria-describedby="manual-desc"
        >
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 p-2 bg-blue-100 rounded-lg group-hover:bg-blue-200 transition-colors">
              <FiEdit3 className="w-6 h-6 text-blue-600" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-gray-900 mb-1">
                Manual Definition
              </div>
              <div className="text-sm text-gray-600" id="manual-desc">
                Create a new metadata tag from scratch with custom properties
              </div>
            </div>
          </div>
        </button>

        <button
          onClick={() => setSelectedMode(GRAPH_PANEL_MODES.LIBRARY)}
          className="w-full group text-left px-5 py-4 hover:bg-green-50 hover:border-green-200 rounded-xl border-2 border-gray-200 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-300"
          role="radio"
          aria-checked="false"
          aria-describedby="library-desc"
        >
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 p-2 bg-green-100 rounded-lg group-hover:bg-green-200 transition-colors">
              <FiBookOpen className="w-6 h-6 text-green-600" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-gray-900 mb-1">
                Metadata Library
              </div>
              <div className="text-sm text-gray-600" id="library-desc">
                Select from your existing metadata tags and reuse them
              </div>
            </div>
          </div>
        </button>

        <button
          onClick={() => setSelectedMode(GRAPH_PANEL_MODES.AUTO)}
          className="w-full group text-left px-5 py-4 hover:bg-purple-50 hover:border-purple-200 rounded-xl border-2 border-gray-200 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-300"
          role="radio"
          aria-checked="false"
          aria-describedby="auto-desc"
        >
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 p-2 bg-purple-100 rounded-lg group-hover:bg-purple-200 transition-colors">
              <FiZap className="w-6 h-6 text-purple-600" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-gray-900 mb-1">
                AI Suggestions
              </div>
              <div className="text-sm text-gray-600" id="auto-desc">
                Get AI-powered metadata suggestions based on your data
              </div>
            </div>
          </div>
        </button>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (selectedMode) {
      case GRAPH_PANEL_MODES.MANUAL:
        return (
          <TagEditor
            showHeader={false}
            includeNewTagButton={true}
            tagInEditor={tagInEditor}
            setTagInEditor={setTagInEditor}
            isTagEditorCollapsed={isTagEditorCollapsed}
            setIsTagEditorCollapsed={setIsTagEditorCollapsed}
            activeTagEditorSection={activeTagEditorSection}
            setActiveTagEditorSection={setActiveTagEditorSection}
            handleAddNodeApplied={handleAddNodeApplied}
            selectedNodeData={selectedNodeData}
            context="graph"
            editingExistingTag={false}
            isGraphContext={false}
          />
        );
      case GRAPH_PANEL_MODES.LIBRARY:
        return (
          <CompactTagsTable
            tagInEditor={tagInEditor}
            setTagInEditor={setTagInEditor}
          />
        );
      case GRAPH_PANEL_MODES.AUTO:
        return (
          <AutoSuggestedMetadata
            generatingSchema={generatingSchema}
            setGeneratingSchema={setGeneratingSchema}
          />
        );
      default:
        return <ModeSelector />;
    }
  };

  return (
    <div
      className="absolute right-0 top-4 bg-white border border-gray-200 shadow-xl rounded-lg flex flex-col z-50 animate-slideInRight w-[28vw] min-w-[400px] max-w-[500px] max-h-[calc(100vh-1rem)]"
      role="dialog"
      aria-modal="true"
      aria-labelledby="panel-title"
    >
      {/* Close button */}
      <button
        onClick={handleClose}
        className="absolute top-3 right-3 z-10 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-gray-300"
        aria-label="Close panel"
        title="Close panel (Esc)"
      >
        <FiX className="w-5 h-5" />
      </button>

      {/* Main content container */}
      <div className="flex flex-col overflow-hidden">
        {!selectedMode ? (
          <ModeSelector />
        ) : (
          <>
            {/* Header */}
            <div className="flex-shrink-0 px-6 py-4 border-b border-gray-100 bg-gray-50">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setSelectedMode(null)}
                  className="p-2 hover:bg-white hover:shadow-sm rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-gray-300"
                  aria-label="Go back"
                  title="Go back to mode selection"
                >
                  <FiArrowLeft className="w-5 h-5 text-gray-600" />
                </button>
                <div>
                  <h2
                    className="text-lg font-semibold text-gray-900"
                    id="panel-title"
                  >
                    {selectedMode === GRAPH_PANEL_MODES.MANUAL &&
                      "Define New Tag"}
                    {selectedMode === GRAPH_PANEL_MODES.LIBRARY &&
                      "Select from Library"}
                    {selectedMode === GRAPH_PANEL_MODES.AUTO &&
                      "AI Suggestions"}
                  </h2>
                  <p className="text-sm text-gray-600 mt-1">
                    {selectedMode === GRAPH_PANEL_MODES.MANUAL &&
                      "Create a custom metadata tag"}
                    {selectedMode === GRAPH_PANEL_MODES.LIBRARY &&
                      "Choose from existing tags"}
                    {selectedMode === GRAPH_PANEL_MODES.AUTO &&
                      "Get AI-powered suggestions"}
                  </p>
                </div>
              </div>
            </div>

            {/* Content area */}
            <div className="flex flex-col flex-1">
              <div
                className={`${
                  selectedMode === GRAPH_PANEL_MODES.AUTO
                    ? "flex-1 overflow-y-auto"
                    : "overflow-y-auto max-h-[60vh]"
                }`}
              >
                {renderContent()}
              </div>
              {selectedMode === GRAPH_PANEL_MODES.LIBRARY && (
                <div className="p-6 border-t border-gray-100 bg-white">
                  <button
                    className={`w-full flex items-center justify-center gap-2 px-6 py-3 rounded-lg text-sm font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                      !tagInEditor || Object.keys(tagInEditor).length === 0
                        ? "bg-gray-100 text-gray-400 cursor-not-allowed focus:ring-gray-300"
                        : "bg-primary text-white hover:bg-emerald-700 focus:ring-primary shadow-sm hover:shadow-md"
                    }`}
                    onClick={handleApply}
                    disabled={
                      !tagInEditor || Object.keys(tagInEditor).length === 0
                    }
                    aria-describedby="apply-button-status"
                  >
                    <FiPlus className="w-4 h-4" />
                    {!tagInEditor || Object.keys(tagInEditor).length === 0
                      ? "Select a tag to continue"
                      : "Add Selected Tag"}
                  </button>
                  <div
                    id="apply-button-status"
                    className="sr-only"
                    aria-live="polite"
                  >
                    {!tagInEditor || Object.keys(tagInEditor).length === 0
                      ? "No tag selected"
                      : "Tag ready to be added"}
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
